import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIPostFolder, BackendFolder, Folder } from "@dataRoomSetup/models";
import { ThunkApiConfig } from "@shared/models/redux";
import { fetchInitialFolders } from "@dataRoomSetup/store/fetchInitiaFolder";
import { fetchInitialFoldersList } from "@dataRoomSetup/store/fetchFolderList";
import { impersonationStateSelector } from "@impersonateUser/store/selectors";
import { postDataroomsetFolders } from "./postDataRoomSetFolders";

const inFlightInitialFolderIds = new Set<number>();

const buildInitialFoldersKey = ({ id, body }: { id: number; body?: APIPostFolder[] }): number | string =>
  body && body.length ? `${id}:${JSON.stringify(body)}` : id;

const inFlightInitialFolderKeys = new Set<string>();

export const fetchInitialFoldersThunk = createAsyncThunk<
  BackendFolder[],
  { id: number; body?: APIPostFolder[] },
  ThunkApiConfig
>(
  "folders/fetchInitialFolders",
  async ({ id, body }, { dispatch, rejectWithValue, getState }) => {
    const key = buildInitialFoldersKey({ id, body });
    const isBodyKey = typeof key === "string";

    if (isBodyKey) {
      inFlightInitialFolderKeys.add(key as string);
    } else {
      inFlightInitialFolderIds.add(id);
    }

    const state = getState();
    const {
      user: { data: userData },
    } = state;

    // The folder tree is scoped by the user id baked into the `initalizeFolder`
    // URL path. While impersonating, `user.data` is still the admin, so fetch
    // folders for the impersonated user to keep the tree in sync with the
    // documents (which are already scoped to the impersonated user at the
    // request layer).
    const impersonation = impersonationStateSelector(state);
    const entitledUserId = impersonation.isImpersonating && impersonation.user ? impersonation.user.id : userData!.id;

    try {
      const response = await dispatch(fetchInitialFolders(id, entitledUserId, body));

      const data = response as BackendFolder[];

      return data as Folder[];
    } catch (error) {
      return rejectWithValue("Failed to fetch Documents");
    } finally {
      if (isBodyKey) {
        inFlightInitialFolderKeys.delete(key as string);
      } else {
        inFlightInitialFolderIds.delete(id);
      }
    }
  },
  {
    condition: ({ id, body }) => {
      const key = buildInitialFoldersKey({ id, body });

      if (typeof key === "string") {
        return !inFlightInitialFolderKeys.has(key);
      }

      return !inFlightInitialFolderIds.has(id);
    },
  }
);

export const fetchInitialFoldersListThunk = createAsyncThunk<string[], { id: number }, ThunkApiConfig>(
  "folders/fetchInitialFoldersList",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await dispatch(fetchInitialFoldersList(id));

      const data = response;

      return data;
    } catch (error) {
      return rejectWithValue("Failed to fetch Documents");
    }
  }
);

export const postDataRoomsetFoldersThunk = createAsyncThunk<
  BackendFolder[],
  { id: number; body?: APIPostFolder[] },
  ThunkApiConfig
>("folders/postDataRoomsetFolders", async ({ id, body }, { dispatch, rejectWithValue, getState }) => {
  const {
    user: { data: userData },
  } = getState();

  try {
    const response = await dispatch(postDataroomsetFolders(id, userData!.id, body));

    const data = response as BackendFolder[];

    return data as Folder[];
  } catch (error) {
    return rejectWithValue("Failed to fetch Documents");
  }
});
